import * as Yup from "yup";
import { PaymentOption } from "../../../types";
import {
  AGENCY_OTHER_OPTION_ID,
  GIFT_INVESTMENT_OPTION_OTHER_ID,
  PAYROLL_REDUCTION_ID
} from "./constants";

const creditCardNumberRegex =
    /^(?:4[0-9]{12}(?:[0-9]{3})?|[25][1-7][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35|30\d{3})\d{11})$/;

const zipCodeRegex = /^\d{5}(-\d{4})?$/;

// Function to validate expiry date in MM/YY format
const validateExpiryDate = (value) => {
  if (!/^\d{2}\/\d{2}$/.test(value)) {
    return false;
  }
  const [month, year] = value.split("/").map(Number);
  if (month < 1 || month > 12) {
    return false;
  }
  const currentYear = new Date().getFullYear() % 100;
  const currentMonth = new Date().getMonth() + 1;
  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return false;
  }
  return true;
};

const employeePledgeValidationSchema = Yup.object().shape({
  contributionOptionId: Yup.string().when(
    "isNotWillingToContribute",
    ([isNotWillingToContribute], schema) => {
      if (!isNotWillingToContribute)
        return Yup.string().required("Contribution option is required");
      return schema;
    }
  ),
  contributionAmount: Yup.number()
    .when("isNotWillingToContribute", ([isNotWillingToContribute], schema) => {
      if (!isNotWillingToContribute)
        return Yup.number()
          .required("Contribution amount is required")
          .integer("Whole dollar amount only please");
      return schema;
    })
    .when("paymentOptionId", ([paymentOptionId], schema) => {
      if (paymentOptionId === PAYROLL_REDUCTION_ID)
        return Yup.number()
          .required("Contribution amount is required")
          .min(24, "Contribution amount has to be at least 24.")
          .integer("Whole dollar amount only please");
      return schema;
    })
    .when(["leadershipEnrollment", "leadershipMinDonationAmount"], ([leadershipEnrollment, leadershipMinDonationAmount], schema) => {
      if (leadershipEnrollment)
      {
        return Yup.number()
        .required("Contribution amount must meet minimum.")
        .min(Number(leadershipMinDonationAmount??0), `Contribution amount has to be at least ${leadershipMinDonationAmount??0}`)
        .integer("Whole dollar amount only please");
      }
      return schema;
    })
    .when(["stepUpEnrollment", "stepUpMinDonationAmount"], ([stepUpEnrollment, stepUpMinDonationAmount], schema) => {
      if (stepUpEnrollment)
      {
        return Yup.number()
        .required("Contribution amount must meet minimum.")
        .min(Number(stepUpMinDonationAmount??0), `Contribution amount has to be at least ${stepUpMinDonationAmount??0}`)
        .integer("Whole dollar amount only please");
      }
      return schema;
    }),
  paymentOptionId: Yup.string().when(
    "isNotWillingToContribute",
    ([isNotWillingToContribute], schema) => {
      if (!isNotWillingToContribute)
        return Yup.string().required("Payment option is required");
      return schema;
    }
  ),
  isWillingToBeIncludedInCompanyEvents: Yup.string()
    .nullable()
    .when("isWillingToStayAnonymous", ([isWillingToStayAnonymous], schema) => {
      if (isWillingToStayAnonymous === "2")
        return Yup.string().required(
          "Please specify if willing to stay anonymous or be included in company events"
        );
      return schema;
    }),
  // giftInvestmentOther: Yup.string()
  //   .nullable()
  //   .when("giftInvestment", ([giftInvestment], schema) => {
  //     if (giftInvestment.includes(GIFT_INVESTMENT_OPTION_OTHER_ID))
  //       return Yup.string().required("Gift investment other text is required");
  //     return schema;
  //   }),
  creditCardNumber: Yup.string()
    .nullable()
    .when("paymentOptionId", ([paymentOptionId], schema) => {
      if (paymentOptionId === PaymentOption.CreditCard)
        return Yup.string()
          .matches(creditCardNumberRegex, "Credit card number is not valid")
          .required("Credit card number is required");
      return schema;
    }),
  cardHolderFirstName: Yup.string()
    .nullable()
    .when("paymentOptionId", ([paymentOptionId], schema) => {
      if (paymentOptionId === PaymentOption.CreditCard)
        return Yup.string().required("Card holder First Name is required");
      return schema;
    }),
  cardHolderLastName: Yup.string()
    .nullable()
    .when("paymentOptionId", ([paymentOptionId], schema) => {
      if (paymentOptionId === PaymentOption.CreditCard)
        return Yup.string().required("Card holder Last Name is required");
      return schema;
    }),
  expirationDate: Yup.string()
    .nullable()
    .when("paymentOptionId", ([paymentOptionId], schema) => {
      if (paymentOptionId === PaymentOption.CreditCard)
        return Yup.string()
          .test("expiryDate", "Expiry date is not valid", (value) =>
            validateExpiryDate(value)
          )
          .required("Expiry date is required");
      return schema;
    }),
  cvv: Yup.string()
    .nullable()
    .when("paymentOptionId", ([paymentOptionId], schema) => {
      if (paymentOptionId === PaymentOption.CreditCard)
        return Yup.string()
          .matches(/^\d{3,4}$/, "Card Security Code must be 3 or 4 digits")
          .required("Card Security Code is required");
      return schema;
    }),
  zip: Yup.string()
    .nullable()
    .when("paymentOptionId", ([paymentOptionId], schema) => {
      if (paymentOptionId === PaymentOption.CreditCard)
        return Yup.string()
          .matches(zipCodeRegex, "Zip code is not valid")
          .required("Zip Code is required");
      return schema;
    }),
  giftInvestment: Yup.array()
    .min(1, "Investment is required")
    .nullable()
    .when("giftInvestmentAmount", (giftInvestmentAmount, schema) => {
      if (
        giftInvestmentAmount[0]?.filter(
          (x) => x.giftInvestmentId === GIFT_INVESTMENT_OPTION_OTHER_ID
        )?.length === 1
      )
        return Yup.array().min(
          giftInvestmentAmount[0]?.length + 1,
          "Agency is required"
        );
      return schema;
    }),
  isWillingToStayAnonymous: Yup.string()
    .nullable()
    .when("isNotWillingToContribute", ([isNotWillingToContribute], schema) => {
      if (!isNotWillingToContribute)
        return Yup.string().required("Anonymous selection is required");
      return schema;
    }),
  spouseName: Yup.string()
    .nullable()
    .when("isWillingToCombineSpouseContribution", {
        is: (fieldValue) => String(fieldValue) === "1",
        then: (schema) => schema.required("Name is required"),
        otherwise: (schema) => schema,
    }),
  spouseWorkPlace: Yup.string()
    .nullable()
    .when("isWillingToCombineSpouseContribution", {
        is: (fieldValue) => String(fieldValue) === "1",
        then: (schema) => schema.required("Work Place is required"),
        otherwise: (schema) => schema,
    }),
  spousePledgeAmount: Yup.string()
    .nullable()
    .when("isWillingToCombineSpouseContribution", {
        is: (fieldValue) => String(fieldValue) === "1",
        then: (schema) => schema.required("Amount is required"),
        otherwise: (schema) => schema,
    }),
  giftInvestmentAmount: Yup.array().of(
    Yup.object().shape({
      otherAgencyName: Yup.string()
        .nullable()
        .when("agencyId", ([agencyId], schema) => {
          if (agencyId === AGENCY_OTHER_OPTION_ID)
            return Yup.string().required("Other Agency Name is required");
          return schema;
        }),
    })
  ),
});

export default employeePledgeValidationSchema;
